/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ProfileSectionAction from "./ProfileSectionAction";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { iconUrl, defaultImg } from "../utils/icons";

function getGenusImage(genusName) {
  if (!genusName) return defaultImg;
  return iconUrl(`${genusName}.png`);
}

function hasCompleteTaxonomy(taxonomy) {
  return !!(
    taxonomy?.genus &&
    taxonomy?.family &&
    taxonomy?.order &&
    taxonomy?.className &&
    taxonomy?.phylum &&
    taxonomy?.kingdom
  );
}

export default function ProfileGenusCards({
  isOwnProfile,
  profileUID,
  profile,
  stats,
  favoriteGenera,
  setFavoriteGenera,
  sectionLoading,
  sectionProgress,
  SectionRefreshOverlay,
  showGenusHint,
  setShowGenusHint,
  editingGenera,
  setEditingGenera,
  genusModalOpen,
  setGenusModalOpen,
  genusPickerOpen,
  setGenusPickerOpen,
  genusSlotIndex,
  setGenusSlotIndex,
  selectedGenus,
  setSelectedGenus,
  savingGenus,
  setSavingGenus,
  resolveTaxonomy,
  navigate,
  saveOwnProfileCache,
  readOwnProfileCache,
  setProfile,
  speciesWithPhotos,
  quizSpecies,
  collections,
  recentUploads,
  collectionMeta,
  unlockedTitles,
}) {
  const [genusFilters, setGenusFilters] = useState({
    kingdom: "",
    phylum: "",
    className: "",
    order: "",
    family: "",
  });
  const [genusSortMode, setGenusSortMode] = useState("alphabetical");
  const [taxonomyNotice, setTaxonomyNotice] = useState("");

  const allGenusEntries = useMemo(() => {
    if (!genusModalOpen && !genusPickerOpen) return [];

    return Object.keys(stats?.counts?.genus || {}).map((genus) => {
      const taxonomy = resolveTaxonomy(genus);

      return {
        genus,
        count: stats?.counts?.genus?.[genus] || 0,
        taxonomy,
        isInTaxonomy: hasCompleteTaxonomy(taxonomy),
      };
    });
  }, [stats, resolveTaxonomy, genusModalOpen, genusPickerOpen]);

  const genusFilterOptions = useMemo(() => {
    const kingdoms = [
      ...new Set(
        allGenusEntries.map((entry) => entry.taxonomy?.kingdom).filter(Boolean),
      ),
    ].sort();

    const phyla = [
      ...new Set(
        allGenusEntries
          .filter(
            (entry) =>
              !genusFilters.kingdom ||
              entry.taxonomy?.kingdom === genusFilters.kingdom,
          )
          .map((entry) => entry.taxonomy?.phylum)
          .filter(Boolean),
      ),
    ].sort();

    const classes = [
      ...new Set(
        allGenusEntries
          .filter(
            (entry) =>
              (!genusFilters.kingdom ||
                entry.taxonomy?.kingdom === genusFilters.kingdom) &&
              (!genusFilters.phylum ||
                entry.taxonomy?.phylum === genusFilters.phylum),
          )
          .map((entry) => entry.taxonomy?.className)
          .filter(Boolean),
      ),
    ].sort();

    const orders = [
      ...new Set(
        allGenusEntries
          .filter(
            (entry) =>
              (!genusFilters.kingdom ||
                entry.taxonomy?.kingdom === genusFilters.kingdom) &&
              (!genusFilters.phylum ||
                entry.taxonomy?.phylum === genusFilters.phylum) &&
              (!genusFilters.className ||
                entry.taxonomy?.className === genusFilters.className),
          )
          .map((entry) => entry.taxonomy?.order)
          .filter(Boolean),
      ),
    ].sort();

    const families = [
      ...new Set(
        allGenusEntries
          .filter(
            (entry) =>
              (!genusFilters.kingdom ||
                entry.taxonomy?.kingdom === genusFilters.kingdom) &&
              (!genusFilters.phylum ||
                entry.taxonomy?.phylum === genusFilters.phylum) &&
              (!genusFilters.className ||
                entry.taxonomy?.className === genusFilters.className) &&
              (!genusFilters.order ||
                entry.taxonomy?.order === genusFilters.order),
          )
          .map((entry) => entry.taxonomy?.family)
          .filter(Boolean),
      ),
    ].sort();

    return {
      kingdom: kingdoms,
      phylum: phyla,
      className: classes,
      order: orders,
      family: families,
    };
  }, [allGenusEntries, genusFilters]);

  const visibleGenusEntries = useMemo(() => {
    const filtered = allGenusEntries.filter((entry) => {
      const taxonomy = entry.taxonomy || {};

      return (
        (!genusFilters.kingdom || taxonomy.kingdom === genusFilters.kingdom) &&
        (!genusFilters.phylum || taxonomy.phylum === genusFilters.phylum) &&
        (!genusFilters.className ||
          taxonomy.className === genusFilters.className) &&
        (!genusFilters.order || taxonomy.order === genusFilters.order) &&
        (!genusFilters.family || taxonomy.family === genusFilters.family)
      );
    });

    return [...filtered].sort((a, b) => {
      if (genusSortMode === "count") {
        return b.count - a.count || a.genus.localeCompare(b.genus);
      }

      return a.genus.localeCompare(b.genus);
    });
  }, [allGenusEntries, genusFilters, genusSortMode]);

  const hasActiveGenusFilters = Object.values(genusFilters).some(Boolean);

  function formatTaxonLabel(value) {
    if (!value) return "";

    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function showMissingTaxonomyNotice(genus, selection = false) {
    setTaxonomyNotice(
      selection
        ? `${genus} cannot be selected as a display card because it is not currently represented in the BioIndex taxonomy.`
        : `${genus} is not currently represented in the BioIndex taxonomy.`,
    );
  }

  function openGenusPage(genus) {
    const taxonomy = resolveTaxonomy(genus);

    if (!hasCompleteTaxonomy(taxonomy)) {
      showMissingTaxonomyNotice(genus);
      return;
    }

    setTaxonomyNotice("");
    setGenusModalOpen(false);

    navigate(
      `/explore/${profileUID}/kingdom/${taxonomy.kingdom}/phylum/${taxonomy.phylum}/class/${taxonomy.className}/order/${taxonomy.order}/family/${taxonomy.family}/genus/${taxonomy.genus}`,
    );
  }

  return (
    <>
      {taxonomyNotice && (
        <div
          role="alert"
          className="fixed bottom-4 left-1/2 z-[1200] flex w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-xl"
        >
          <span className="min-w-0 flex-1">{taxonomyNotice}</span>
          <button
            type="button"
            onClick={() => setTaxonomyNotice("")}
            className="shrink-0 text-lg leading-none text-red-500 transition hover:text-red-700"
            aria-label="Dismiss taxonomy notice"
          >
            ×
          </button>
        </div>
      )}

      {/* FAVORITE GENERA */}
      <div className="relative rounded-3xl border border-[#93ca87]/40 bg-white p-5 shadow-lg sm:p-8">
        <SectionRefreshOverlay
          show={sectionLoading.taxonomy}
          progress={sectionProgress.taxonomy}
          label="Refreshing genus cards"
        />

        <div className="mb-5 sm:mb-7">
          <div className="bioindex-section-heading">
            <h2
              className="text-2xl leading-none text-[#374049] sm:text-3xl"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Unlocked Genus Cards
            </h2>

            <div className="bioindex-section-actions" aria-label="Genus card actions">
              <ProfileSectionAction
                kind="info"
                label="About Genus Cards"
                active={showGenusHint}
                onClick={() => setShowGenusHint((prev) => !prev)}
              />
              <ProfileSectionAction
                kind="view"
                label="View All Genus Cards"
                onClick={() => {
                  if (!editingGenera) setGenusModalOpen(true);
                }}
                disabled={editingGenera}
              />
              {isOwnProfile && (
                <ProfileSectionAction
                  kind={editingGenera ? "check" : "edit"}
                  label={editingGenera ? "Done Editing Genus Cards" : "Change Display Cards"}
                  active={editingGenera}
                  onClick={() => setEditingGenera((prev) => !prev)}
                />
              )}
            </div>
          </div>

          {showGenusHint && (
            <div className="bioindex-section-hint" role="note">
              <p>
                <strong>Genus cards</strong> are unlocked when you add a photo
                of a species belonging to that genus. Displayed cards are
                shortcuts to their genus pages, and you can choose favorites.
              </p>
              <p className="mt-2">
                <strong>Artwork note:</strong> These illustrations are currently
                AI generated. Some genera lack illustrations. As BioIndex
                becomes financially sustainable, the goal is to commission
                scientific artists to replace the AI artwork.
              </p>
            </div>
          )}

          {favoriteGenera.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-10">
              {isOwnProfile
                ? "You haven’t unlocked any genera yet."
                : "This user hasn’t unlocked any genera yet."}
            </div>
          ) : (
            <div className="bioindex-featured-genus-grid mx-auto grid w-full max-w-[1290px] grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-6 lg:gap-[18px]">
              {favoriteGenera.map((genus) => {
                const taxonomy = resolveTaxonomy(genus);
                const isInTaxonomy = hasCompleteTaxonomy(taxonomy);

                return (
                  <button
                    key={genus}
                    onClick={() => {
                      if (editingGenera) {
                        setGenusSlotIndex(favoriteGenera.indexOf(genus));
                        setSelectedGenus(null);
                        setGenusFilters({
                          kingdom: "",
                          phylum: "",
                          className: "",
                          order: "",
                          family: "",
                        });
                        setGenusSortMode("alphabetical");
                        setGenusPickerOpen(true);
                        return;
                      }

                      openGenusPage(genus);
                    }}
                    title={
                      isInTaxonomy
                        ? undefined
                        : `${genus} is not currently represented in the BioIndex taxonomy.`
                    }
                    className={`group relative flex aspect-[4/5] w-full flex-none flex-col overflow-hidden rounded-2xl border bg-white text-center transition-all duration-200 ${
                      isInTaxonomy
                        ? "border-[#93ca87]/60 shadow-[0_6px_18px_rgba(55,64,73,0.10)] hover:border-[#24c3be]/70"
                        : "border-red-300 shadow-[0_6px_18px_rgba(185,28,28,0.08)]"
                    } ${
                      editingGenera
                        ? "ring-2 ring-[#24c3be] hover:-translate-y-0.5"
                        : "hover:-translate-y-1 hover:shadow-[0_10px_24px_rgba(55,64,73,0.16)]"
                    }`}
                  >
                    <div
                      className={`absolute inset-x-6 top-0 z-10 h-1 rounded-b-full ${
                        isInTaxonomy ? "bg-[#24c3be]" : "bg-red-500"
                      }`}
                    />

                    {!isInTaxonomy && !editingGenera && (
                      <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 whitespace-nowrap rounded-full border border-red-200 bg-red-600 px-2.5 py-1 text-[9px] font-medium text-white shadow-sm">
                        Taxonomy pending
                      </div>
                    )}

                    {editingGenera && (
                      <>
                        <div className="pointer-events-none absolute inset-0 z-10 bg-[#24c3be]/10" />

                        <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 whitespace-nowrap rounded-full border border-[#24C3BE]/40 bg-[#374049] px-2.5 py-1 text-[9px] font-medium text-white shadow-sm sm:text-[10px]">
                          Click to replace
                        </div>
                      </>
                    )}

                    <div className="flex h-[84%] w-full items-center justify-center bg-white p-3 sm:p-4">
                      <img
                        src={getGenusImage(genus)}
                        onError={(e) => (e.currentTarget.src = defaultImg)}
                        alt={genus}
                        className="max-h-full max-w-full object-contain transition-transform duration-200 group-hover:scale-[1.035]"
                      />
                    </div>

                    <div
                      className={`flex h-[16%] w-full items-center justify-center border-t px-2 ${
                        isInTaxonomy
                          ? "border-[#93ca87]/35 bg-[#374049]"
                          : "border-red-600 bg-red-700"
                      }`}
                    >
                      <h2
                        className="max-w-full truncate text-[clamp(0.7rem,3.4vw,0.88rem)] italic leading-none text-white sm:text-base"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {genus}
                      </h2>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {genusModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[1000] overflow-y-auto overscroll-contain bg-[#374049]">
            <div className="min-h-[100dvh] w-full bg-[#374049] px-4 py-6 sm:px-8 sm:py-8 lg:px-12">
              <div className="w-full max-w-5xl mx-auto">
                <div className="rounded-2xl border border-[#93CA87]/40 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 text-center sm:text-left">
                      <h3
                        className="text-3xl sm:text-4xl text-[#24C3BE] leading-none"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        All Unlocked Genera
                      </h3>

                      <p className="mt-2 text-xs sm:text-sm text-[#374049]/60">
                        Showing {visibleGenusEntries.length} of{" "}
                        {allGenusEntries.length} genus cards
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setGenusModalOpen(false)}
                      className="self-center shrink-0 rounded-xl border border-[#24C3BE] bg-[#24C3BE] px-5 py-2.5 text-sm font-medium text-white shadow-inner transition hover:bg-[#01767a] hover:border-[#01767a] sm:self-auto"
                    >
                      Close
                    </button>
                  </div>

                  <div className="my-4 h-px w-full bg-[#93CA87]/30 sm:my-5" />

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                        Kingdom
                      </span>

                      <select
                        value={genusFilters.kingdom}
                        onChange={(e) =>
                          setGenusFilters({
                            kingdom: e.target.value,
                            phylum: "",
                            className: "",
                            order: "",
                            family: "",
                          })
                        }
                        className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                      >
                        <option value="">All</option>
                        {genusFilterOptions.kingdom.map((value) => (
                          <option key={value} value={value}>
                            {formatTaxonLabel(value)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                        Phylum
                      </span>

                      <select
                        value={genusFilters.phylum}
                        onChange={(e) =>
                          setGenusFilters((prev) => ({
                            ...prev,
                            phylum: e.target.value,
                            className: "",
                            order: "",
                            family: "",
                          }))
                        }
                        className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                      >
                        <option value="">All</option>
                        {genusFilterOptions.phylum.map((value) => (
                          <option key={value} value={value}>
                            {formatTaxonLabel(value)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                        Class
                      </span>

                      <select
                        value={genusFilters.className}
                        onChange={(e) =>
                          setGenusFilters((prev) => ({
                            ...prev,
                            className: e.target.value,
                            order: "",
                            family: "",
                          }))
                        }
                        className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                      >
                        <option value="">All</option>
                        {genusFilterOptions.className.map((value) => (
                          <option key={value} value={value}>
                            {formatTaxonLabel(value)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                        Order
                      </span>

                      <select
                        value={genusFilters.order}
                        onChange={(e) =>
                          setGenusFilters((prev) => ({
                            ...prev,
                            order: e.target.value,
                            family: "",
                          }))
                        }
                        className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                      >
                        <option value="">All</option>
                        {genusFilterOptions.order.map((value) => (
                          <option key={value} value={value}>
                            {formatTaxonLabel(value)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                        Family
                      </span>

                      <select
                        value={genusFilters.family}
                        onChange={(e) =>
                          setGenusFilters((prev) => ({
                            ...prev,
                            family: e.target.value,
                          }))
                        }
                        className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                      >
                        <option value="">All</option>
                        {genusFilterOptions.family.map((value) => (
                          <option key={value} value={value}>
                            {formatTaxonLabel(value)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block min-w-0">
                      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                        Order cards
                      </span>

                      <select
                        value={genusSortMode}
                        onChange={(e) => setGenusSortMode(e.target.value)}
                        className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                      >
                        <option value="alphabetical">Alphabetical</option>
                        <option value="count">Most species</option>
                      </select>
                    </label>
                  </div>

                  {hasActiveGenusFilters && (
                    <div className="mt-4 flex justify-center sm:justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setGenusFilters({
                            kingdom: "",
                            phylum: "",
                            className: "",
                            order: "",
                            family: "",
                          })
                        }
                        className="rounded-xl border border-[#93CA87]/40 bg-white px-4 py-2 text-xs font-medium text-[#24C3BE] transition hover:border-[#24C3BE]/50 hover:bg-[#24C3BE]/10"
                      >
                        Clear filters
                      </button>
                    </div>
                  )}
                </div>

                {visibleGenusEntries.length === 0 ? (
                  <div className="py-16 text-center">
                    <p
                      className="text-2xl text-[#FCFFF6]"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      No genus cards match these filters
                    </p>

                    <button
                      type="button"
                      onClick={() =>
                        setGenusFilters({
                          kingdom: "",
                          phylum: "",
                          className: "",
                          order: "",
                          family: "",
                        })
                      }
                      className="mt-4 rounded-xl bg-[#24c3be] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#01767a] transition"
                    >
                      Show all genera
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 grid w-full grid-cols-2 gap-3 pb-8 sm:gap-4 lg:grid-cols-4 lg:gap-5 justify-items-center">
                    {visibleGenusEntries.map(
                      ({ genus, count, isInTaxonomy }) => (
                        <button
                          type="button"
                          key={genus}
                          onClick={() => openGenusPage(genus)}
                          title={
                            isInTaxonomy
                              ? `Open ${genus}`
                              : `${genus} is not currently represented in the BioIndex taxonomy.`
                          }
                          className={`relative rounded-2xl border shadow-sm transition w-40 sm:w-48 lg:w-56 flex flex-col items-center text-center overflow-hidden hover:shadow-lg hover:-translate-y-[2px] ${
                            isInTaxonomy
                              ? "border-white/20 bg-white"
                              : "border-red-300 bg-red-50"
                          }`}
                        >
                          <div
                            className={`absolute top-3 right-3 z-20 min-w-7 h-7 px-2 flex items-center justify-center rounded-full text-white text-xs font-semibold shadow-sm border ${
                              isInTaxonomy
                                ? "border-[#93CA87]/50 bg-[#374049]"
                                : "border-red-200 bg-red-700"
                            }`}
                          >
                            {count}
                          </div>

                          {!isInTaxonomy && (
                            <div className="absolute left-3 top-3 z-20 rounded-full border border-red-200 bg-red-600 px-2.5 py-1 text-[9px] font-medium text-white shadow-sm">
                              Taxonomy pending
                            </div>
                          )}

                          <div className="relative w-full h-40 sm:h-48 lg:h-56 flex items-center justify-center p-4 sm:p-5 lg:p-6">
                            <img
                              src={getGenusImage(genus)}
                              onError={(e) =>
                                (e.currentTarget.src = defaultImg)
                              }
                              alt={genus}
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>

                          <h2
                            className="w-full max-w-full px-2 pb-5 text-center text-[clamp(0.7rem,4vw,1.25rem)] sm:text-[clamp(0.8rem,2.1vw,1.25rem)] lg:text-[clamp(0.85rem,1.15vw,1.25rem)] leading-none text-[#374049] italic whitespace-nowrap overflow-hidden text-ellipsis"
                            style={{ fontFamily: "Cormorant Garamond" }}
                          >
                            {genus}
                          </h2>
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {genusPickerOpen &&
        createPortal(
          <div className="fixed inset-0 z-[1000] overflow-y-auto overscroll-contain bg-[#374049]">
            <div className="min-h-[100dvh] w-full bg-[#374049] px-4 py-6 sm:px-8 sm:py-8 lg:px-12">
              <div className="w-full max-w-5xl mx-auto">
                {!selectedGenus ? (
                  <>
                    <div className="rounded-2xl border border-[#93CA87]/40 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-5">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 text-center sm:text-left">
                          <h3
                            className="text-3xl sm:text-4xl text-[#24C3BE] leading-none"
                            style={{ fontFamily: "Cormorant Garamond" }}
                          >
                            Select a Genus Card
                          </h3>

                          <p className="mt-2 text-xs sm:text-sm text-[#374049]/60">
                            Choose a genus to replace{" "}
                            <span
                              className="italic text-[#374049]"
                              style={{ fontFamily: "Cormorant Garamond" }}
                            >
                              {favoriteGenera[genusSlotIndex]}
                            </span>
                            . Showing {visibleGenusEntries.length} of{" "}
                            {allGenusEntries.length} genus cards.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setSelectedGenus(null);
                            setGenusPickerOpen(false);
                          }}
                          className="self-center shrink-0 rounded-xl border border-[#24C3BE] bg-[#24C3BE] px-5 py-2.5 text-sm font-medium text-white shadow-inner transition hover:bg-[#01767a] hover:border-[#01767a] sm:self-auto"
                        >
                          Close
                        </button>
                      </div>

                      <div className="my-4 h-px w-full bg-[#93CA87]/30 sm:my-5" />

                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        <label className="block min-w-0">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                            Kingdom
                          </span>

                          <select
                            value={genusFilters.kingdom}
                            onChange={(e) =>
                              setGenusFilters({
                                kingdom: e.target.value,
                                phylum: "",
                                className: "",
                                order: "",
                                family: "",
                              })
                            }
                            className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                          >
                            <option value="">All</option>
                            {genusFilterOptions.kingdom.map((value) => (
                              <option key={value} value={value}>
                                {formatTaxonLabel(value)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="block min-w-0">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                            Phylum
                          </span>

                          <select
                            value={genusFilters.phylum}
                            onChange={(e) =>
                              setGenusFilters((prev) => ({
                                ...prev,
                                phylum: e.target.value,
                                className: "",
                                order: "",
                                family: "",
                              }))
                            }
                            className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                          >
                            <option value="">All</option>
                            {genusFilterOptions.phylum.map((value) => (
                              <option key={value} value={value}>
                                {formatTaxonLabel(value)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="block min-w-0">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                            Class
                          </span>

                          <select
                            value={genusFilters.className}
                            onChange={(e) =>
                              setGenusFilters((prev) => ({
                                ...prev,
                                className: e.target.value,
                                order: "",
                                family: "",
                              }))
                            }
                            className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                          >
                            <option value="">All</option>
                            {genusFilterOptions.className.map((value) => (
                              <option key={value} value={value}>
                                {formatTaxonLabel(value)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="block min-w-0">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                            Order
                          </span>

                          <select
                            value={genusFilters.order}
                            onChange={(e) =>
                              setGenusFilters((prev) => ({
                                ...prev,
                                order: e.target.value,
                                family: "",
                              }))
                            }
                            className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                          >
                            <option value="">All</option>
                            {genusFilterOptions.order.map((value) => (
                              <option key={value} value={value}>
                                {formatTaxonLabel(value)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="block min-w-0">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                            Family
                          </span>

                          <select
                            value={genusFilters.family}
                            onChange={(e) =>
                              setGenusFilters((prev) => ({
                                ...prev,
                                family: e.target.value,
                              }))
                            }
                            className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                          >
                            <option value="">All</option>
                            {genusFilterOptions.family.map((value) => (
                              <option key={value} value={value}>
                                {formatTaxonLabel(value)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="block min-w-0">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#374049]/65">
                            Order cards
                          </span>

                          <select
                            value={genusSortMode}
                            onChange={(e) => setGenusSortMode(e.target.value)}
                            className="w-full min-w-0 rounded-xl border border-[#374049]/20 bg-white px-3 py-2 text-sm text-[#374049]"
                          >
                            <option value="alphabetical">Alphabetical</option>
                            <option value="count">Most species</option>
                          </select>
                        </label>
                      </div>

                      {hasActiveGenusFilters && (
                        <div className="mt-4 flex justify-center sm:justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              setGenusFilters({
                                kingdom: "",
                                phylum: "",
                                className: "",
                                order: "",
                                family: "",
                              })
                            }
                            className="rounded-xl border border-[#93CA87]/40 bg-white px-4 py-2 text-xs font-medium text-[#24C3BE] transition hover:border-[#24C3BE]/50 hover:bg-[#24C3BE]/10"
                          >
                            Clear filters
                          </button>
                        </div>
                      )}
                    </div>

                    {visibleGenusEntries.length === 0 ? (
                      <div className="py-16 text-center">
                        <p
                          className="text-2xl text-[#FCFFF6]"
                          style={{ fontFamily: "Cormorant Garamond" }}
                        >
                          No genus cards match these filters
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            setGenusFilters({
                              kingdom: "",
                              phylum: "",
                              className: "",
                              order: "",
                              family: "",
                            })
                          }
                          className="mt-4 rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#01767a]"
                        >
                          Show all genera
                        </button>
                      </div>
                    ) : (
                      <div className="mt-6 grid w-full grid-cols-2 gap-3 pb-8 sm:gap-4 lg:grid-cols-4 lg:gap-5 justify-items-center">
                        {visibleGenusEntries.map(
                          ({ genus, count, isInTaxonomy }) => {
                            const isCurrentlyDisplayed =
                              favoriteGenera[genusSlotIndex] === genus;

                            return (
                              <button
                                type="button"
                                key={genus}
                                onClick={() => {
                                  if (isCurrentlyDisplayed) return;

                                  if (!isInTaxonomy) {
                                    showMissingTaxonomyNotice(genus, true);
                                    return;
                                  }

                                  setSelectedGenus(genus);
                                }}
                                disabled={isCurrentlyDisplayed}
                                title={
                                  isInTaxonomy
                                    ? undefined
                                    : `${genus} cannot be selected because it is not represented in the BioIndex taxonomy.`
                                }
                                className={`group relative rounded-2xl border shadow-sm transition w-40 sm:w-48 lg:w-56 flex flex-col items-center text-center overflow-hidden ${
                                  isCurrentlyDisplayed
                                    ? "border-[#24C3BE] opacity-65 cursor-default"
                                    : isInTaxonomy
                                      ? "border-[#93CA87]/40 bg-white hover:shadow-lg hover:-translate-y-[2px]"
                                      : "cursor-not-allowed border-red-300 bg-red-50 hover:border-red-400"
                                }`}
                              >
                                <div className="absolute top-3 right-3 z-20 min-w-7 h-7 px-2 flex items-center justify-center rounded-full bg-[#374049] text-white text-xs font-semibold shadow-sm border border-[#93CA87]/50">
                                  {count}
                                </div>

                                {isCurrentlyDisplayed && (
                                  <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 whitespace-nowrap rounded-full border border-[#24C3BE]/40 bg-[#24C3BE] px-3 py-1 text-[10px] sm:text-[11px] font-medium text-white shadow-sm">
                                    Currently displayed
                                  </div>
                                )}

                                {!isInTaxonomy && !isCurrentlyDisplayed && (
                                  <div className="absolute left-3 top-3 z-20 rounded-full border border-red-200 bg-red-600 px-2.5 py-1 text-[9px] font-medium text-white shadow-sm">
                                    Unavailable
                                  </div>
                                )}

                                {!isCurrentlyDisplayed && isInTaxonomy && (
                                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#374049]/0 transition group-hover:bg-[#374049]/10">
                                    <div className="translate-y-2 rounded-full border border-[#24C3BE]/40 bg-[#374049] px-4 py-2 text-xs font-medium text-white opacity-0 shadow-sm transition group-hover:translate-y-0 group-hover:opacity-100">
                                      Select card
                                    </div>
                                  </div>
                                )}

                                <div className="relative w-full h-40 sm:h-48 lg:h-56 flex items-center justify-center p-4 sm:p-5 lg:p-6">
                                  <img
                                    src={getGenusImage(genus)}
                                    onError={(e) =>
                                      (e.currentTarget.src = defaultImg)
                                    }
                                    alt={genus}
                                    className="max-h-full max-w-full object-contain"
                                  />
                                </div>

                                <h2
                                  className="w-full max-w-full px-2 pb-5 text-center text-[clamp(0.7rem,4vw,1.25rem)] sm:text-[clamp(0.8rem,2.1vw,1.25rem)] lg:text-[clamp(0.85rem,1.15vw,1.25rem)] leading-none text-[#374049] italic whitespace-nowrap overflow-hidden text-ellipsis"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                >
                                  {genus}
                                </h2>
                              </button>
                            );
                          },
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mx-auto max-w-lg rounded-2xl border border-[#93CA87]/40 bg-white p-5 shadow-sm sm:p-6">
                    <div className="text-center">
                      <p className="text-xs font-medium uppercase tracking-wide text-[#24C3BE]">
                        Replace genus card
                      </p>

                      <h3
                        className="mt-2 text-3xl text-[#374049] leading-none"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        Display <span className="italic">{selectedGenus}</span>?
                      </h3>

                      <p className="mt-3 text-sm text-[#374049]/60">
                        This will replace{" "}
                        <span
                          className="italic text-[#374049]"
                          style={{ fontFamily: "Cormorant Garamond" }}
                        >
                          {favoriteGenera[genusSlotIndex]}
                        </span>{" "}
                        in your displayed genus cards.
                      </p>
                    </div>

                    <div className="mx-auto mt-5 w-40 overflow-hidden rounded-2xl border border-[#93CA87]/40 bg-white shadow-sm sm:w-48">
                      <div className="flex h-40 w-full items-center justify-center p-4 sm:h-48 sm:p-5">
                        <img
                          src={getGenusImage(selectedGenus)}
                          onError={(e) => (e.currentTarget.src = defaultImg)}
                          alt={selectedGenus}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>

                      <h2
                        className="w-full px-2 pb-5 text-center text-xl italic leading-none text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {selectedGenus}
                      </h2>
                    </div>

                    <div className="mt-6 flex flex-col-reverse justify-center gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => setSelectedGenus(null)}
                        disabled={savingGenus}
                        className="rounded-xl border border-[#93CA87]/40 bg-white px-5 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-[#24C3BE]/10 disabled:opacity-50"
                      >
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !hasCompleteTaxonomy(resolveTaxonomy(selectedGenus))
                          ) {
                            showMissingTaxonomyNotice(selectedGenus, true);
                            setSelectedGenus(null);
                            return;
                          }

                          setSavingGenus(true);

                          try {
                            const updated = [...favoriteGenera];
                            updated[genusSlotIndex] = selectedGenus;

                            setFavoriteGenera(updated);

                            const now = Date.now();

                            await setDoc(
                              doc(db, "profile", auth.currentUser.uid),
                              {
                                favoriteGenera: updated,
                                sectionUpdatedAt: {
                                  ...(profile.sectionUpdatedAt || {}),
                                  profile: now,
                                },
                              },
                              { merge: true },
                            );

                            setProfile((prev) => ({
                              ...prev,
                              favoriteGenera: updated,
                              sectionUpdatedAt: {
                                ...(prev.sectionUpdatedAt || {}),
                                profile: now,
                              },
                            }));

                            saveOwnProfileCache({
                              profile: {
                                ...profile,
                                favoriteGenera: updated,
                                sectionUpdatedAt: {
                                  ...(profile.sectionUpdatedAt || {}),
                                  profile: now,
                                },
                              },
                              favoriteGenera: updated,
                              stats,
                              speciesWithPhotos,
                              quizSpecies,
                              collections,
                              recentUploads,
                              collectionMeta,
                              unlockedTitles,
                              sectionUpdatedAt: {
                                ...(readOwnProfileCache()?.sectionUpdatedAt ||
                                  {}),
                                profile: now,
                              },
                            });

                            setSelectedGenus(null);
                            setGenusPickerOpen(false);
                          } finally {
                            setSavingGenus(false);
                          }
                        }}
                        disabled={savingGenus}
                        className="rounded-xl border border-[#24C3BE] bg-[#24C3BE] px-5 py-2.5 text-sm font-medium text-white shadow-inner transition hover:bg-[#01767a] hover:border-[#01767a] disabled:cursor-wait disabled:opacity-60"
                      >
                        {savingGenus ? "Saving..." : "Display this card"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
